import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import { Formik } from 'formik';
import { SelectRoleValidationSchema } from '../../config/FormValidaton/AuthValidationSchema';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';

import ButtonLoading from '@mui/lab/LoadingButton';

import { RootState } from 'store';
import { useGetRoleAccessToken, useLogout } from 'services/v1/Auth';
import { useGetMyPersonalizationLogin, useGetTenantConfigDataMutation } from 'services/v1/Tenant/ConfigurationService';
import {
  RoleInterface,
  RoleType,
  setAccessToken,
  setRole,
  setSelectedTenant,
  setUserBehaviourRole,
  logout,
} from 'store/reducer/authReducer';
import { resetHomeNavigatinState } from 'store/reducer/homeReducer';
import { setTenantNavigation } from 'store/reducer/uiReducer';
import { setConfig, setSetupStatus } from 'store/reducer/settingReducer';
import { setAuthHeaderToken, setAuthTenantId } from 'libs/Axios';
import { DASHBOARD_TYPE_CONSTANT } from 'constant/DashboardTypeConstant';
import { TENANT_CONFIGURATION_KEY, TENANT_INITIAL_SETUP_STATUS } from 'constant/TenantConfigConstant';
import { PATH_CONSTANT } from 'constant/PathConstant';
import { DEFAUL_LOGIN_REDIRECT_URL } from 'constant/PermissonConstant';
import { getUserRoleBehaviour } from './SelectRolePage';
import { getQuickAccessNavigateURL } from 'components/LayoutComponent/VerticalLayout/QuickAccess';

interface formValues {
  role: string;
  tenantId: string;
}

interface IRoleOptions {
  label: string;
  value: string;
}

interface ITenantOption {
  value: string;
  label: string;
}

const dropdownHelperTextStyle = {
  color: '#3B4797',
  fontWeight: '600',
};

const formInitialValues: formValues = {
  role: '',
  tenantId: '',
};

const ROLE_ICON_MAP: Record<string, React.ReactNode> = {
  Admin: <AdminPanelSettingsOutlinedIcon fontSize='small' />,
  Builder: <BuildOutlinedIcon fontSize='small' />,
  Manager: <VisibilityOutlinedIcon fontSize='small' />,
  Packager: <Inventory2OutlinedIcon fontSize='small' />,
  Tester: <BugReportOutlinedIcon fontSize='small' />,
  Support: <SupportAgentOutlinedIcon fontSize='small' />,
  Trainer: <SchoolOutlinedIcon fontSize='small' />,
};

function getRoleIcon(roleName: string) {
  return ROLE_ICON_MAP[roleName] || <PersonOutlinedIcon fontSize='small' />;
}

type ChangeRoleComponentProps = {
  setSelectedRoleProps?: (role: RoleType) => void;
  title?: string | null;
  onSelectRole?: (role: RoleType | '') => void;
};

export function ChangeRoleComponent(props: ChangeRoleComponentProps) {
  const queryClient = useQueryClient();
  const { setSelectedRoleProps, title = 'Authentication has been successful', onSelectRole } = props;
  const { roles, tenants, selectedTenant, role } = useSelector((state: RootState) => state.auth);
  const { mutateAsync: getRoleAccessToken, isLoading: isSubmitting } = useGetRoleAccessToken();
  const { mutateAsync: refetchPersonalization, isLoading: isLoadingPersonalization } = useGetMyPersonalizationLogin();
  const { mutateAsync: getTenantConfig, isLoading: isFetchingTenantConfig } = useGetTenantConfigDataMutation();
  const formikRef = useRef<any>(null);
  const tenantAutocompleteRef = useRef<HTMLInputElement>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { mutate: logoutUser } = useLogout();

  const handleLogoutRedirect = () => {
    dispatch(resetHomeNavigatinState());
    setTimeout(() => {
      dispatch(setTenantNavigation(false));
      dispatch(logout());
      window.location.href = '/auth/login';
    }, 200);
  };

  const handleLogout = () => {
    logoutUser(undefined, {
      onSuccess: handleLogoutRedirect,
      onError: handleLogoutRedirect,
    });
  };

  const resetQueryCache = () => {
    queryClient.resetQueries();
  };

  const handleSubmit = async (data: formValues) => {
    const selectedTenantId = data.tenantId?.split('@')?.[0] || '';
    const selectedRoleName = data.tenantId?.split('@')?.[1] || '';

    const selectedTenant = tenants.find(
      (tenant) => tenant.tenant.id === selectedTenantId && tenant.tenant.roleName === selectedRoleName,
    );

    if (selectedTenant) {
      dispatch(resetHomeNavigatinState());
      setAuthTenantId(selectedTenant.tenant.id);

      const res = await getRoleAccessToken({
        roleName: selectedTenant.tenant.roleName,
        tenantId: selectedTenant.tenant.id,
      });
      const roleAccessToken = res.data.data;
      setAuthHeaderToken(roleAccessToken);
      dispatch(setAccessToken(roleAccessToken));
      dispatch(setSelectedTenant(selectedTenant));
      const roleBehaviour = getUserRoleBehaviour(selectedTenant.roleType as RoleType, selectedTenant) as RoleType;

      if (selectedTenant.tenant.dashboardType === DASHBOARD_TYPE_CONSTANT.DASHBOARD_TENANT) {
        const tenantConfig = await getTenantConfig();
        const initialSetupStatus = tenantConfig.data?.find(
          (config) => config.id === TENANT_CONFIGURATION_KEY.INITIAL_SETUP_STATUS,
        )?.value;

        initialSetupStatus && dispatch(setSetupStatus(initialSetupStatus));
        dispatch(setConfig(tenantConfig.data || []));

        if (initialSetupStatus === TENANT_INITIAL_SETUP_STATUS.INITIAL) {
          dispatch(setUserBehaviourRole(roleBehaviour));
          navigate(PATH_CONSTANT.AUTH.INITIAL_SETUP);
          return;
        }
      }

      onSelectRole && onSelectRole(selectedTenant.roleType as RoleType);

      if (selectedTenant.tenant.dashboardType !== DASHBOARD_TYPE_CONSTANT.DASHBOARD_SYSTEM) {
        const userPersonalization = await refetchPersonalization();
        const defaultURL = getQuickAccessNavigateURL(
          userPersonalization?.data.defaultLoginView,
          undefined,
          undefined,
          role,
        );
        if (defaultURL) {
          navigate(defaultURL);
          return;
        }
      }
      dispatch(setUserBehaviourRole(roleBehaviour));
      navigate(DEFAUL_LOGIN_REDIRECT_URL[roleBehaviour]);
      dispatch(setRole(selectedTenant.roleType as RoleType));
      resetQueryCache();
    }
  };

  const roleExistWithTenants = useMemo(() => {
    const uniqueRoles = tenants
      .map((tenant) => tenant.tenant.roleName)
      .filter((role, index, roles) => roles.indexOf(role) === index);
    return uniqueRoles;
  }, [tenants]);

  const rolesOption: IRoleOptions[] = useMemo(() => {
    if (roles.length === 0) return [];

    const uniqueRoles: RoleInterface[] = [];
    const map = new Map();
    for (const role of roles) {
      if (!map.has(role.roleName)) {
        map.set(role.roleName, true);
        uniqueRoles.push(role);
      }
    }

    return uniqueRoles
      .map((role) => ({
        value: role.roleName,
        label: role.roleName,
      }))
      .filter((roleOption) => roleExistWithTenants.includes(roleOption.label))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [roles, roleExistWithTenants]);

  const tenantsOption: ITenantOption[] = useMemo(() => {
    if (tenants.length === 0) return [];

    return tenants
      .filter((tenant) => tenant.tenant.roleName === selectedRole)
      .map((tenant) => {
        return {
          value: `${tenant.tenant.id}@${tenant.tenant.roleName}`,
          label: tenant.tenant.tenantName || (tenant.tenant.systemName as string),
        };
      })
      .sort((a, b) => a.label?.localeCompare(b.label?.toString()));
  }, [tenants, selectedRole]);

  const currentActiveTenantValue = selectedTenant.tenant.id
    ? `${selectedTenant.tenant.id}@${selectedTenant.tenant.roleName}`
    : '';

  useEffect(() => {
    if (tenantsOption.length === 0) {
      formikRef.current?.setFieldValue('tenantId', '');
    }
    // Auto-select when only one tenant available
    if (tenantsOption.length === 1) {
      formikRef.current?.setFieldValue('tenantId', tenantsOption[0].value);
    }
  }, [tenantsOption]);

  useEffect(() => {
    if (rolesOption.length > 0) {
      formikRef.current?.setFieldValue('role', rolesOption[0].value);
      setSelectedRole(rolesOption[0].value);
      // We are finding the role object that matches the name to pass the correct RoleType if possible
      const roleObj = roles.find((r) => r.roleName === rolesOption[0].value);
      if (roleObj && setSelectedRoleProps) {
        setSelectedRoleProps(roleObj.roleType as RoleType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesOption]);

  useEffect(() => {
    if (selectedTenant.roleType) {
      formikRef.current?.setFieldValue('role', selectedTenant.tenant.roleName);
      formikRef.current?.setFieldValue('tenantId', `${selectedTenant.tenant.id}@${selectedTenant.tenant.roleName}`);
      setSelectedRole(selectedTenant.tenant.roleName);
      setSelectedRoleProps && setSelectedRoleProps(selectedTenant.roleType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant.roleType, selectedTenant.tenant.id, selectedTenant.tenant.roleName]);

  const currentActiveRoleName = selectedTenant.tenant.roleName || '';

  const handleRoleAutocompleteChange = (
    newValue: IRoleOptions | null,
    setFieldValue: (field: string, value: string) => void,
  ) => {
    const roleName = newValue?.value || '';
    setFieldValue('role', roleName);
    setSelectedRole(roleName);
    setFieldValue('tenantId', '');

    const roleObj = roles.find((r) => r.roleName === roleName);
    if (roleObj && setSelectedRoleProps) {
      setSelectedRoleProps(roleObj.roleType as RoleType);
    }

    // Auto-focus tenant dropdown after role selection
    if (roleName) {
      setTimeout(() => tenantAutocompleteRef.current?.focus(), 100);
    }
  };

  return (
    <>
      <Typography component='h4' variant='h4' color='#2C2A29' mb={4}>
        {title}
      </Typography>

      <Formik
        innerRef={formikRef}
        initialValues={formInitialValues}
        onSubmit={handleSubmit}
        validationSchema={SelectRoleValidationSchema}
      >
        {({ setFieldValue, handleSubmit, values }) => (
          <form
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          >
            {/* Searchable Role Dropdown */}
            <FormHelperText sx={dropdownHelperTextStyle}>Select your role</FormHelperText>
            <FormControl fullWidth margin='dense'>
              <Autocomplete
                disabled={isSubmitting || isFetchingTenantConfig}
                options={rolesOption}
                value={rolesOption.find((opt) => opt.value === values.role) || null}
                onChange={(_e, newValue) => handleRoleAutocompleteChange(newValue, setFieldValue)}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, val) => option.value === val.value}
                renderOption={(props, option) => {
                  const isCurrentActiveRole = option.value === currentActiveRoleName;
                  return (
                    <Box
                      component='li'
                      {...props}
                      key={option.value}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: isCurrentActiveRole ? '#F0FBF7 !important' : undefined,
                      }}
                    >
                      <Box sx={{ display: 'flex', color: '#667085' }}>{getRoleIcon(option.value)}</Box>
                      <Typography variant='body2' sx={{ flex: 1 }}>
                        {option.label}
                      </Typography>
                      {isCurrentActiveRole && (
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#3ABB93' }} />
                      )}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Search role...'
                    autoFocus
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position='start'>
                            <BadgeOutlinedIcon sx={{ fontSize: 20, color: '#667085' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>

            <Divider sx={{ my: 2, borderColor: '#E4E7EC' }} />

            {/* Searchable Tenant Dropdown */}
            <FormHelperText sx={dropdownHelperTextStyle}>Select your customer solution</FormHelperText>
            <FormControl fullWidth margin='dense'>
              <Autocomplete
                disabled={!selectedRole || isSubmitting || isFetchingTenantConfig}
                options={tenantsOption}
                value={tenantsOption.find((opt) => opt.value === values.tenantId) || null}
                onChange={(_e, newValue) => {
                  setFieldValue('tenantId', newValue?.value || '');
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, val) => option.value === val.value}
                noOptionsText={
                  !selectedRole
                    ? 'Select a role first'
                    : 'No customer solution available for this role'
                }
                renderOption={(props, option) => {
                  const isCurrentActiveTenant = option.value === currentActiveTenantValue;
                  return (
                    <Box
                      component='li'
                      {...props}
                      key={option.value}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: isCurrentActiveTenant ? '#F0FBF7 !important' : undefined,
                      }}
                    >
                      <BusinessOutlinedIcon sx={{ fontSize: 18, color: '#667085' }} />
                      <Typography variant='body2' sx={{ flex: 1 }}>
                        {option.label}
                      </Typography>
                      {isCurrentActiveTenant && (
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#3ABB93' }} />
                      )}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={tenantAutocompleteRef}
                    placeholder={
                      !selectedRole
                        ? 'Select a role first'
                        : tenantsOption.length === 0
                          ? 'No customer solution available'
                          : 'Search customer solution...'
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position='start'>
                            <BusinessOutlinedIcon sx={{ fontSize: 20, color: '#667085' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>

            <Tooltip
              title={!SelectRoleValidationSchema.isValidSync(values) ? 'Please select both a role and a customer solution' : ''}
              arrow
              placement='top'
            >
              <span>
                <ButtonLoading
                  loading={isSubmitting || isFetchingTenantConfig || isLoadingPersonalization}
                  type='submit'
                  fullWidth
                  loadingPosition='start'
                  onClick={() => handleSubmit()}
                  variant='main-login'
                  sx={{
                    mt: 4,
                    mb: 2,
                  }}
                  disabled={!SelectRoleValidationSchema.isValidSync(values)}
                >
                  Next
                </ButtonLoading>
              </span>
            </Tooltip>

            <Button
              variant='text'
              fullWidth
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                mt: 1,
                color: 'text.secondary',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Not your account? Logout
            </Button>
          </form>
        )}
      </Formik>
    </>
  );
}
